import React, { useState, useRef, useEffect } from 'react'

import { useIntl } from 'react-intl'
import { captureException } from '@sentry/react'
import { LoanRole } from '@matech/thebigpos-sdk'

import { LoanUserRow } from '@/components/loanAppDetails/loan-users-table/types'
import { UserRole } from '@/types'
import Dialog from './Dialog'
import ShareLoanUserForm, {
	type ShareLoanUserRef,
	type HandleSubmitAddUserParams,
} from '../ShareLoanUserForm'
import ShareLoanList from '../ShareLoanList/ShareLoanList'
import { Roles } from '../../services/client'
import { SelectChangeEvent } from '../formControls/Select'

export type ModalShareLoanProps = {
	visible: boolean
	isDraft?: boolean
	onClose: () => void
	addUser?: {
		onBeforeAdd?: (user: LoanUserRow, role: LoanRole) => boolean
		onAdd?: (user: LoanUserRow, role: LoanRole) => void
		isLoading?: boolean
	}
	roleChange?: {
		onChange?: (userId: string, role: LoanRole) => void
	}
	revokeUser?: {
		onRevoke?: (userId: string) => void
		isLoading?: boolean
	}
	submit?: {
		onSubmit?: (data: UserRole[]) => Promise<void>
		isLoading?: boolean
	}
	isLoading?: boolean
	initialSelectedUsersRoles?: UserRole[]
	showActions?: boolean
}

const USERS_FIELD_FILTER_ROLES = [
	Roles.loanOfficer,
	Roles.loanProcessor,
	Roles.loanOfficerAssistant,
]

// ToDo: Unify these methods to use in src/components/modals/ModalUserGroupsMembers.tsx
// But without context about shareLoan
const ModalShareLoan = ({
	visible,
	isDraft,
	onClose,
	addUser,
	roleChange,
	revokeUser,
	submit,
	isLoading,
	initialSelectedUsersRoles,
	showActions = true,
}: ModalShareLoanProps) => {
	const { formatMessage } = useIntl()
	const shareLoanUserFormRef = useRef<ShareLoanUserRef>(null)

	const [selectedLocalUserList, setSelectedLocalUserList] = useState<
		UserRole[]
	>([])

	const modalTitle = formatMessage({
		id: isDraft ? 'share_loan.incomplete_title' : 'share_loan.title',
	})
	const disableTableButtons =
		addUser?.isLoading || revokeUser?.isLoading
	const disableSubmitButton =
		submit?.isLoading || !selectedLocalUserList[0]
	const disableCloseButton = disableTableButtons || submit?.isLoading

	const handleDialogExit = () => setSelectedLocalUserList([])

	const handleDialogEntered = () =>
		shareLoanUserFormRef.current?.selectUsersFieldFocus()

	const handleRevokeUser = async (userId: string) => {
		try {
			if (revokeUser?.onRevoke) {
				revokeUser.onRevoke(userId)
				return
			}

			setSelectedLocalUserList((prev) =>
				prev.filter((userRole) => userRole.user.id !== userId)
			)
		} catch (error) {
			captureException(
				`Fail to revoke user: ${JSON.stringify(error)}`
			)
		}
	}

	const handleAddUser = ({
		user,
		role,
	}: HandleSubmitAddUserParams) => {
		if (addUser?.onBeforeAdd) {
			const resultBeforeAddUser = addUser?.onBeforeAdd(user, role)

			if (!resultBeforeAddUser) return
		}

		if (addUser?.onAdd) {
			addUser.onAdd(user, role)
		} else {
			setSelectedLocalUserList((prev) => [
				...prev,
				{
					user,
					assignedRole: role,
				},
			])
		}

		shareLoanUserFormRef.current?.clearForm()
	}

	const handleValidateUserChange = (
		user: LoanUserRow | null
	): boolean => {
		if (!user) return false

		return selectedLocalUserList.some(
			(userRole) => userRole.user.id === user.id
		)
	}

	// Put on hook
	const handleClose = (
		e?: object,
		reason?: 'backdropClick' | 'escapeKeyDown'
	) => {
		if (addUser?.isLoading) return

		if (reason === 'backdropClick') return

		onClose()
	}

	const handleSubmit = async () => {
		if (!selectedLocalUserList || selectedLocalUserList.length === 0)
			return

		await submit?.onSubmit?.(selectedLocalUserList)

		handleClose()
	}

	const handleRoleChange = (
		e: SelectChangeEvent<unknown>,
		userId: string
	) => {
		const assignedRole = e.target.value as LoanRole

		if (roleChange?.onChange) {
			roleChange.onChange(userId, assignedRole)
			return
		}

		setSelectedLocalUserList((prev) =>
			prev.map((userRole) =>
				userRole.user.id === userId
					? {
							...userRole,
							assignedRole,
						}
					: userRole
			)
		)
	}

	useEffect(() => {
		if (initialSelectedUsersRoles) {
			setSelectedLocalUserList(initialSelectedUsersRoles)
		}
	}, [initialSelectedUsersRoles])

	return (
		<Dialog
			aria-labelledby="share-loan-dialog-title"
			fullWidth
			title={modalTitle}
			open={visible}
			onClose={handleClose}
			maxWidth="md"
			showActions={showActions}
			closeButtonProps={{
				text: formatMessage({
					id: 'remove_record.cancel',
				}),
				onClick: handleClose,
				disabled: disableCloseButton,
			}}
			submitButtonProps={{
				text: formatMessage({
					id: 'share_loan.title',
				}),
				onClick: handleSubmit,
				loading: submit?.isLoading,
				disabled: disableSubmitButton,
			}}
			TransitionProps={{
				onEntered: handleDialogEntered,
				onExit: handleDialogExit,
			}}
		>
			<div style={{ marginBottom: 20 }}>
				{formatMessage({
					id: 'share_loan.description',
				})}
			</div>

			<ShareLoanUserForm
				loanUserFormRef={shareLoanUserFormRef}
				loading={addUser?.isLoading}
				disabled={disableTableButtons}
				showRolesBasedOnUserRole
				handleSubmitAddUser={handleAddUser}
				onValidateUserChange={handleValidateUserChange}
				filterRolesUserField={USERS_FIELD_FILTER_ROLES}
			/>

			<ShareLoanList
				loading={isLoading}
				data={selectedLocalUserList}
				submitting={submit?.isLoading}
				onRevokeUser={handleRevokeUser}
				onRoleItemChange={handleRoleChange}
			/>
		</Dialog>
	)
}

export default ModalShareLoan
